import {useMediaQuery} from "@mui/material";
import {useEffect, useState} from "react";
import {Floorplan} from "../../../api/apiClient";
import {SWITCH_HEIGHT_PX, SWITCH_HIT_AREA_PADDING_PX} from "./LabeledSwitch";

/** Which navigation shell to use, driven by the space available for it rather than by the device. */
export type NavLayout =
    | "sheet"           // Phone portrait: swipes up from the bottom over the panorama.
    | "panel-switched"  // Phone landscape: side panel, one section at a time via the switcher.
    | "panel-stacked";  // Tablet and desktop: side panel with both sections stacked under headings.

// Sizes the shell and its contents both need to agree on.
/** The horizontal gutter every row of navigation content starts from, in MUI spacing units. */
export const PANEL_GUTTER = 3;

/** Vertical breathing room between rows of navigation content. */
export const ROW_SPACING_PX = 12;

/** Gap under the floor tabs, which FloorTabs applies and the sheet's height has to allow for. */
export const FLOOR_TABS_PADDING_BOTTOM_PX = ROW_SPACING_PX;

export const PANEL_WIDTH_PX = 320;
export const PANEL_WIDTH_SHORT_PX = 280;
export const SHEET_HEADER_HEIGHT_PX = 68;

// Its own row, so the grabber sits in the same place whether or not there is a switcher below it.
export const SHEET_GRABBER_ROW_HEIGHT_PX = 26;

/** Width of the panel's handle, which stands outside the panel and stays put when it closes. */
export const PANEL_HANDLE_WIDTH_PX = 36;

/** Far enough to count as a deliberate drag rather than a tap that wobbled. */
export const TAP_TOLERANCE_PX = 4;

/**
 * How long the sheet and the panel take to settle once released, and with them anything that has to keep
 * pace with them.
 */
export const PANEL_SLIDE_TRANSITION = "0.3s ease";

const MUI_SPACING_PX = 8;

/** MUI's medium Chip, which is what the floor tabs render. */
const MUI_CHIP_HEIGHT_PX = 32;

// The widths and heights the layouts switch on. Written as MUI does it, a hair under the breakpoint, so
// that a viewport landing exactly on it belongs to the larger layout rather than to both or to neither.
const BREAKPOINT_EPSILON_PX = 0.05;

/** Below this width there is no room for a side panel beside the panorama. */
const PANEL_MIN_WIDTH_PX = 600;

/** Below this height a side panel cannot show both sections at once and needs the switcher. */
const STACKED_MIN_HEIGHT_PX = 560;

const PHONE_WIDTH_QUERY = `(max-width: ${PANEL_MIN_WIDTH_PX - BREAKPOINT_EPSILON_PX}px)`;
const PHONE_PORTRAIT_QUERY = `(orientation: portrait) and ${PHONE_WIDTH_QUERY}`;
const SHORT_LANDSCAPE_QUERY =
    `(orientation: landscape) and (max-height: ${STACKED_MIN_HEIGHT_PX - BREAKPOINT_EPSILON_PX}px)`;

/** The switch brings its own hit-area padding, which counts towards the spacing its row needs. */
export const SWITCH_ROW_PADDING_PX = ROW_SPACING_PX - SWITCH_HIT_AREA_PADDING_PX;

// The rows above and below the plan in the sheet: height the plan does not get. Derived from what those
// rows actually render, so restyling either one cannot leave the sheet sizing itself to a stale number.
const FLOOR_TABS_HEIGHT_PX = MUI_CHIP_HEIGHT_PX + FLOOR_TABS_PADDING_BOTTOM_PX;
const SWITCH_ROW_HEIGHT_PX = SWITCH_HEIGHT_PX + (2 * SWITCH_ROW_PADDING_PX);

// Tuned by eye rather than derived: the share used when there is no plan to size against, the ceiling
// the sheet never grows past, and the floor it never shrinks below.
const SHEET_FALLBACK_SHARE = 0.48;
const SHEET_MAX_SHARE = 0.75;
const SHEET_MIN_HEIGHT_PX = 320;

function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
}

// The visual viewport is what is actually on screen: on a phone, the window minus the browser's toolbar.
// innerHeight usually agrees, but has no event of its own for the toolbar collapsing.
function measureViewport() {
    const visualViewport = window.visualViewport;

    return {
        width: visualViewport?.width ?? window.innerWidth,
        height: visualViewport?.height ?? window.innerHeight
    };
}

function useViewportSize() {
    const [size, setSize] = useState(measureViewport);

    useEffect(() => {
        const measure = () => setSize(measureViewport());
        const visualViewport = window.visualViewport;

        window.addEventListener("resize", measure);
        visualViewport?.addEventListener("resize", measure);

        return () => {
            window.removeEventListener("resize", measure);
            visualViewport?.removeEventListener("resize", measure);
        };
    }, []);

    return size;
}

export function useNavLayout(): NavLayout {
    // Too narrow for a side panel, so the navigation comes up from the bottom instead.
    const isPhonePortrait = useMediaQuery(PHONE_PORTRAIT_QUERY);

    // Wide enough for a side panel but too short to stack both sections in it. A tablet in landscape is
    // taller than this and falls through to the stacked layout.
    const isShortLandscape = useMediaQuery(SHORT_LANDSCAPE_QUERY);

    if (isPhonePortrait) return "sheet";
    if (isShortLandscape) return "panel-switched";
    return "panel-stacked";
}

/** Whether there is room for a side panel beside the panorama. */
export enum ViewportWidth {
    Narrow,
    Wide
}

/** Width only: a dialog on a host's page is wide and short, and being short is not being a phone. */
export function useViewportWidth(): ViewportWidth {
    return useMediaQuery(PHONE_WIDTH_QUERY) ? ViewportWidth.Narrow : ViewportWidth.Wide;
}

/**
 * Height of the part of the sheet that slides out of view, which is everything below the header. Sized so
 * the plan fills the width between the gutters. In pixels, because the sheet's drag maths works in pixels.
 */
export function useSheetContentHeight(floorPlans: Floorplan[]) {
    const viewport = useViewportSize();

    // Every floor renders at the same width, so the sheet has to fit the tallest of them: the smallest
    // width-to-height ratio.
    const aspectRatios = floorPlans.map(floorPlan => floorPlan.imageWidth / floorPlan.imageHeight)
        .filter(aspectRatio => aspectRatio > 0);

    // The plan spans the viewport between the gutters, one of which sits at either side.
    const planWidthPx = viewport.width - (2 * PANEL_GUTTER * MUI_SPACING_PX);

    // Everything in the sheet that is not the plan, and so has to be added on top of the plan's height.
    const tabsHeightPx = floorPlans.length > 1 ? FLOOR_TABS_HEIGHT_PX : 0;
    const aroundPlanHeightPx = tabsHeightPx + SWITCH_ROW_HEIGHT_PX + SHEET_HEADER_HEIGHT_PX;

    const wantedHeightPx = aspectRatios.length > 0
        ? Math.round(planWidthPx / Math.min(...aspectRatios)) + aroundPlanHeightPx
        : Math.round(viewport.height * SHEET_FALLBACK_SHARE);

    const sheetHeightPx = clamp(wantedHeightPx,
        SHEET_MIN_HEIGHT_PX,
        Math.round(viewport.height * SHEET_MAX_SHARE));

    // The header stays on screen when the sheet is closed, so it is not part of what slides away.
    return sheetHeightPx - SHEET_HEADER_HEIGHT_PX;
}

/** The Floorplan/Rooms switcher only exists where both sections cannot be shown at the same time. */
export function needsTabSwitcher(layout: NavLayout) {
    return layout !== "panel-stacked";
}
